import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TimesheetModel, TimesheetModelFromJson } from '../../models/low-level/timesheet.model';
import { ConcurrentUsageService } from '../../services/concurrent-usage/concurrent-usage.service';

import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TimesheetConcurrentStorageService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly concurrencyStore: ConcurrentUsageService,
    ) {}

    public getTimesheet(storageId: string): Observable<TimesheetModel | undefined> {
        return this.concurrencyStore.readDataForKey(storageId).pipe(
            map((result) => {
                if (result?.length > 0) {
                    const timesheet = new TimesheetModelFromJson(JSON.parse(result[0].value));
                    if (timesheet.saveTimestamp !== result[0].timestamp) {
                        timesheet.saveTimestamp = result[0].timestamp;
                    }
                    return timesheet;
                }
                throw new Error(`[Forms Concurrent Store]: No data found for key ${storageId}`);
            }),
            catchError((error) => {
                const errorMessage = `[Forms Concurrent Store]: Reading form state failed: ${error}`;
                this.log.warn(errorMessage);
                return of(undefined);
            }),
        );
    }

    public saveData<T extends { saveTimestamp: number }>(storageId: string, data: T): Observable<T> {
        const stringifiedData = JSON.stringify(data);
        return this.concurrencyStore.writeDataForKey(storageId, stringifiedData, data.saveTimestamp ?? 0).pipe(
            map(() => data),
            catchError((error) => {
                this.log.warn(`[Forms Concurrent Store]: Saving form state failed: ${error}`);
                return of(data);
            }),
        );
    }

    public loadData<T extends { saveTimestamp: number }>(storageId: string): Observable<T | undefined> {
        return this.concurrencyStore.readDataForKey(storageId).pipe(
            map((result) => {
                if (result?.length > 0) {
                    const data = JSON.parse(result[0].value) as T;
                    if (data.saveTimestamp !== result[0].timestamp) {
                        data.saveTimestamp = result[0].timestamp;
                    }
                    return data;
                }
                throw new Error(`[Forms Concurrent Store]: No data found for key ${storageId}`);
            }),
            catchError((error) => {
                const errorMessage = `[Forms Concurrent Store]: Reading form state failed: ${error}`;
                this.log.warn(errorMessage);
                return of(undefined);
            }),
        );
    }
}
